import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider, Outlet, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect, useRef } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';

import Header from './components/Header';
import Body from './components/Body';
import About from './components/About';
import Error from './components/Error';
import Footer from './components/Footer';
import WhatsappButton from './components/WhatsappButton';

const Contact = lazy(() => import('./components/Contact'));
// Blog is temporarily hidden. Restore these imports together with the routes below.
// const Blog = lazy(() => import('./components/Blog'));
// const BlogPost = lazy(() => import('./components/BlogPost'));
const Calculators = lazy(() => import('./components/Calculators'));
const HealthCheckPage = lazy(() => import('./components/HealthCheckPage'));
const SipCalculatorPage = lazy(() => import('./components/SipCalculatorPage'));
const InsuranceCalculatorPage = lazy(() => import('./components/InsuranceCalculatorPage'));
const EmiCalculatorPage = lazy(() => import('./components/EmiCalculatorPage'));
const TaxRegimePage = lazy(() => import('./components/TaxRegimePage'));
const RetirementCalculatorPage = lazy(() => import('./components/RetirementCalculatorPage'));
const Disclaimer = lazy(() => import('./components/Disclaimer'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));

const PageLoader = () => (
    <div className='page-loading' role='status' aria-live='polite'>
        <span className='page-loading-mark' aria-hidden='true'>
            <span />
            <span />
            <span />
        </span>
        <span className='visually-hidden'>Loading page</span>
    </div>
);

const MotionOutlet = () => {
    const location = useLocation();
    const pageRef = useRef(null);

    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

        const page = pageRef.current;
        if (!page) return;

        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const revealItems = page.querySelectorAll(
            '.section-heading, .why-choose > *, .service-card, .process-section > *, .process-steps article, .calculators-promo-inner, .testimonial-card, .standards-section > *, .standards-details > *, .faq-section > *, .cta-banner > *, .about-hero > *, .about-story > *, .highlight-pill, .tools-hero > *, .calculator-card, .tool-hub-card, .contact-hero > *, .contact-grid > *, .blog-card, .blog-post-body > *, .legal-hero > *, .legal-content > section'
        );

        if (reducedMotion || !('IntersectionObserver' in window)) {
            revealItems.forEach((item) => item.classList.add('is-visible'));
            return;
        }

        revealItems.forEach((item, index) => {
            item.classList.add('reveal-item');
            item.style.setProperty('--reveal-delay', `${Math.min(index % 4, 3) * 55}ms`);
        });

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.classList.add('is-visible');
                    observer.unobserve(entry.target);
                }
            });
        }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });

        revealItems.forEach((item) => observer.observe(item));
        return () => observer.disconnect();
    }, [location.pathname]);

    return (
        <main ref={pageRef} key={location.pathname} className='page-transition'>
            <Outlet />
        </main>
    );
};

const AppLayout = () => {
    return (
        <div className='app'>
            <Header />
            <Suspense fallback={<PageLoader />}>
                <MotionOutlet />
            </Suspense>
            <Footer />
            <WhatsappButton />
        </div>
    );
};

const appRouter = createBrowserRouter([
    {
        path: '/',
        element: <AppLayout />,
        errorElement: <Error />,
        children: [
            {
                path: '/',
                element: <Body />,
            },
            {
                path: '/about',
                element: <About />,
            },
            {
                path: '/contact',
                element: <Contact />,
            },
            // Blog routes are temporarily disabled.
            // {
            //     path: '/blog',
            //     element: <Blog />,
            // },
            // {
            //     path: '/blog/:slug',
            //     element: <BlogPost />,
            // },
            {
                path: '/tools',
                children: [
                    {
                        index: true,
                        element: <Calculators />,
                    },
                    {
                        path: 'health-check',
                        element: <HealthCheckPage />,
                    },
                    {
                        path: 'sip-calculator',
                        element: <SipCalculatorPage />,
                    },
                    {
                        path: 'insurance-calculator',
                        element: <InsuranceCalculatorPage />,
                    },
                    {
                        path: 'emi-calculator',
                        element: <EmiCalculatorPage />,
                    },
                    {
                        path: 'tax-regime',
                        element: <TaxRegimePage />,
                    },
                    {
                        path: 'retirement-calculator',
                        element: <RetirementCalculatorPage />,
                    },
                ],
            },
            {
                path: '/disclaimer',
                element: <Disclaimer />,
            },
            {
                path: '/privacy-policy',
                element: <PrivacyPolicy />,
            },
        ],
    },
]);

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(<RouterProvider router={appRouter} />);
